import type { ChatRoomFilterInput } from '@/types/schema.generated'
import { useApolloClient } from '@vue/apollo-composable'
import { useDocumentVisibility } from '@vueuse/core'
import type { Ref } from 'vue'
import { computed, ref, watch } from 'vue'
import type {
  AssignedChatRoomListQuery,
  FetchChatRoomsOnUseAssignedMatchesQuery,
} from './useAssignedMatches.generated'
import {
  FetchChatRoomsOnUseAssignedMatchesDocument,
  useAssignedChatRoomListLazyQuery,
} from './useAssignedMatches.generated'

import { useChatRoomFilterResult } from '../useChatRoomFilterResult'
import { useUpdatedChatRoom } from './useUpdatedChatRoom'

const ROOMS_PER_PAGE = 30

type ChatRoomEdge = NonNullable<
  NonNullable<
    NonNullable<AssignedChatRoomListQuery['assignedChatRooms']>['edges']
  >[number]
>
type ChatRoomNode = ChatRoomEdge['node']

export function useAssignedMatches(
  viewerId: string,
  entranceGUID: string,
  filterInput: Ref<ChatRoomFilterInput> = ref({})
) {
  const { client: apolloClient } = useApolloClient()
  const variables = computed(() => ({
    first: ROOMS_PER_PAGE,
    filter: filterInput.value,
  }))
  const chatRooms = ref<ChatRoomNode[]>([])
  const {
    load,
    result,
    fetchMore,
    error,
    refetch: _refetch,
    loading,
  } = useAssignedChatRoomListLazyQuery(variables, {
    fetchPolicy: 'no-cache',
  })

  // fetchMoreを使うとfetchMoreで渡されたvariablesで1度クエリが実行され、その後useAssignedChatRoomListQueryで定義されたvariablesでもう一度クエリが実行されてしまい、resultが上書きされたりされなかったりするバグがある
  // そのためページネーションのstate管理は自前でやる
  watch(
    () => result.value,
    value => {
      if (chatRooms.value.length === 0) {
        chatRooms.value =
          value?.assignedChatRooms?.edges
            ?.filter((item): item is NonNullable<typeof item> => !!item)
            .map(item => item.node) || []
        after.value = value?.assignedChatRooms?.pageInfo.endCursor || undefined
      }
    }
  )

  watch(filterInput, () => {
    chatRooms.value = []
  })

  const refetch = async (input?: Parameters<typeof _refetch>[0]) => {
    chatRooms.value = []
    await _refetch(input)
  }

  const documentVisibility = useDocumentVisibility()

  const updatedChatRoomIds = ref<string[]>([])

  watch(
    () => [updatedChatRoomIds.value, documentVisibility.value],
    async ([_chatRoomIds, _documentVisibility]) => {
      if (_chatRoomIds.length === 0) {
        return
      }

      if (_documentVisibility !== 'visible') {
        return
      }

      updatedChatRoomIds.value = []

      const chatRoomIds = Array.from(new Set(_chatRoomIds))
      // チャット一括送信によって、大量のチャットルームが更新されることがある（例えば OA は 150件一気に送るとか）
      // 予想しない大きな数字で送られたときの処理が怖いので、
      // ある程度の量がまとめて更新されたときはもうリストごとまとめて更新してしまう
      if (chatRoomIds.length >= 100) {
        refetch()
        return
      }

      const result =
        await apolloClient.query<FetchChatRoomsOnUseAssignedMatchesQuery>({
          query: FetchChatRoomsOnUseAssignedMatchesDocument,
          variables: {
            ids: chatRoomIds,
          },
          // updatedChatRoomIds が更新されたとき、チャットルームの更新があったということなのでキャッシュを使わない
          fetchPolicy: 'no-cache',
        })

      const updatedChatRooms = result.data.chatRoomsByIDs
      if (!updatedChatRooms) {
        return
      }

      updatedChatRooms.forEach(room => {
        if (!room.match.isAssigned) {
          return
        }

        const index = chatRooms.value.findIndex(r => r.id === room.id)

        if (index === -1) {
          chatRooms.value = [...chatRooms.value, room]
        }

        chatRooms.value = [
          ...chatRooms.value.slice(0, index),
          room,
          ...chatRooms.value.slice(index + 1),
        ]
      })
    }
  )

  const onUpdateChatRoom = async (chatRoomIds: string[]) => {
    updatedChatRoomIds.value = [...updatedChatRoomIds.value, ...chatRoomIds]
  }

  const mutate = async () => {
    if (result.value) {
      await refetch({
        first: result.value.assignedChatRooms?.edges?.length || ROOMS_PER_PAGE,
      })
    }
  }

  const useUpdatedChatRoomConnectedOnce = ref(false)
  useUpdatedChatRoom(viewerId, entranceGUID, onUpdateChatRoom, () => {
    // 一度繋がったあと、再度繋がっているということは切断されていた期間があったということ
    // 切断されていた期間があったということは、onUpdateChatRoom が呼ばれていないチャットルーム更新があったかもしれないので、
    // リストを再取得する
    if (useUpdatedChatRoomConnectedOnce.value) {
      mutate()
    }

    useUpdatedChatRoomConnectedOnce.value = true
  })

  const { matches } = useChatRoomFilterResult(chatRooms, filterInput)

  const isLoadingNextPage = ref(false)

  const hasNextPage = computed(
    () => result.value?.assignedChatRooms?.pageInfo?.hasNextPage
  )

  const initialError = computed(() =>
    error.value ? 'エラーが発生しました' : null
  )

  const after = ref<string | undefined>(undefined)

  const wrappedFetchMore = async () => {
    if (isLoadingNextPage.value) return

    isLoadingNextPage.value = true
    try {
      const data = await fetchMore({
        variables: {
          first: ROOMS_PER_PAGE,
          after: after.value,
        },
      })

      if (data) {
        chatRooms.value = [
          ...chatRooms.value,
          ...(data.data.assignedChatRooms?.edges
            ?.filter((item): item is NonNullable<typeof item> => !!item)
            .map(item => item?.node) || []),
        ]
        after.value =
          data.data.assignedChatRooms?.pageInfo.endCursor ?? undefined
      }
    } finally {
      isLoadingNextPage.value = false
    }
  }

  return {
    load,
    data: matches,
    error: initialError,
    isLoadingNextPage,
    loading,
    mutate,
    fetchMore: wrappedFetchMore,
    hasNextPage,
    onUpdateChatRoom,
  }
}
